// parseEchoCodes.js - Extract echo codes from report text

import {
  getCachedCodeMap,
  getCategoryForCode
} from './echo-codes/echoCodesLoader';
import { ModifierEnumSets } from './echo-codes/modifier-enums.ts';

/**
 * Known modifier words that can appear near codes
 * Generated dynamically from all modifier enum sets
 */
const MODIFIERS = Array.from(
  new Set(
    Object.values(ModifierEnumSets)
      .flat()
      .map(m => m.toLowerCase())
  )
);

/**
 * Extract echo codes from report text
 * Looks for patterns like: RV-0028, LV-0062, MV-0161, etc.
 *
 * @param {string} reportText - The report text to parse
 * @returns {Object} Object with codes grouped by category
 */
export function extractEchoCodes(reportText) {
  if (!reportText || typeof reportText !== 'string') {
    return {};
  }

  // Regex to find code patterns: 2-3 capital letters, hyphen, 4 digits
  // Examples: RV-0028, LV-0062, MV-0161, AV-0092
  const codeRegex = /\b([A-Z]{2,3})-(\d{4})\b/g;

  const codeMap = getCachedCodeMap();
  const foundCodes = new Map(); // Use Map to prevent duplicates

  let match;
  while ((match = codeRegex.exec(reportText)) !== null) {
    const code = match[0]; // Full match (e.g., "RV-0028")
    const matchIndex = match.index;

    // Validate that this code exists in our JSON files
    if (!codeMap.has(code)) {
      continue; // Skip invalid codes
    }

    // Get the category for this code
    const category = getCategoryForCode(code);
    if (!category) {
      continue; // Skip if category can't be determined
    }

    // Look for modifiers near the code (within 50 characters before or after)
    const contextStart = Math.max(0, matchIndex - 50);
    const contextEnd = Math.min(reportText.length, matchIndex + code.length + 50);
    const context = reportText.substring(contextStart, contextEnd).toLowerCase();

    // Find modifier in context
    let modifier = null;
    for (const mod of MODIFIERS) {
      if (context.includes(mod.toLowerCase())) {
        modifier = mod;
        break;
      }
    }

    // Store the code (Map prevents duplicates automatically)
    if (!foundCodes.has(code)) {
      foundCodes.set(code, {
        code: code,
        modifier: modifier,
        source: 'auto',
        category: category
      });
    }
  }

  // Group codes by category (in display order)
  const groupedCodes = {
    vg: [],
    vd: [],
    oreillettes: [],
    valve_mitrale: [],
    valve_tricuspide: [],
    valve_aortique: [],
    valve_pulmonaire: [],
    gros_vaisseaux: [],
    pericarde: [],
    divers: []
  };

  // Organize codes into categories
  foundCodes.forEach(codeObj => {
    const category = codeObj.category;
    if (groupedCodes[category]) {
      groupedCodes[category].push({
        code: codeObj.code,
        modifier: codeObj.modifier,
        source: 'auto'
      });
    }
  });

  return groupedCodes;
}

/**
 * Merge auto-extracted codes with manually-added codes
 * Preserves manual codes and adds new auto-detected codes
 *
 * @param {Object} autoCodes - Auto-extracted codes from text
 * @param {Object} existingCodes - Existing codes from structuredData
 * @returns {Object} Merged codes
 */
export function mergeCodesWithExisting(autoCodes, existingCodes) {
  if (!existingCodes || typeof existingCodes !== 'object') {
    return autoCodes;
  }

  const merged = {
    vg: [],
    vd: [],
    oreillettes: [],
    valve_mitrale: [],
    valve_tricuspide: [],
    valve_aortique: [],
    valve_pulmonaire: [],
    gros_vaisseaux: [],
    pericarde: [],
    divers: []
  };

  // Categories to process
  const categories = Object.keys(merged);

  categories.forEach(category => {
    const autoCat = autoCodes[category] || [];
    const existingCat = existingCodes[category] || [];

    // Create a map of existing codes for quick lookup
    const existingCodeMap = new Map();
    existingCat.forEach(codeObj => {
      existingCodeMap.set(codeObj.code, codeObj);
    });

    // Add all existing codes first (preserves manual codes)
    existingCat.forEach(codeObj => {
      merged[category].push({ ...codeObj });
    });

    // Add auto-detected codes that don't already exist
    autoCat.forEach(codeObj => {
      if (!existingCodeMap.has(codeObj.code)) {
        merged[category].push({ ...codeObj });
      } else {
        // If code exists and is manual, keep it as is
        // If code exists and is auto, update the modifier if found in text
        const existing = existingCodeMap.get(codeObj.code);
        if (existing.source === 'auto' && codeObj.modifier && !existing.modifier) {
          // Normalize modifier to lowercase for consistency with enum values
          existing.modifier = codeObj.modifier.toLowerCase();
        }
      }
    });
  });

  return merged;
}

/**
 * Add a code manually to the codes structure
 * Validates that code doesn't already exist
 *
 * @param {Object} codes - Current codes structure
 * @param {string} code - Code to add (e.g., "RV-0028")
 * @param {string|null} modifier - Optional modifier
 * @returns {Object} Object with {success: boolean, codes: Object, message: string}
 */
export function addCodeManually(codes, code, modifier = null) {
  if (!code || typeof code !== 'string') {
    return {
      success: false,
      codes: codes,
      message: 'Code invalide'
    };
  }

  const codeMap = getCachedCodeMap();

  // Validate code exists
  if (!codeMap.has(code)) {
    return {
      success: false,
      codes: codes,
      message: 'Ce code n\'existe pas dans la base de données'
    };
  }

  // Get category
  const category = getCategoryForCode(code);
  if (!category) {
    return {
      success: false,
      codes: codes,
      message: 'Catégorie non trouvée pour ce code'
    };
  }

  // Check if code already exists
  const categoryArray = codes[category] || [];
  const exists = categoryArray.some(c => c.code === code);

  if (exists) {
    return {
      success: false,
      codes: codes,
      message: 'Ce code est déjà présent'
    };
  }

  // Add the code
  const newCodes = { ...codes };
  if (!newCodes[category]) {
    newCodes[category] = [];
  }

  newCodes[category] = [
    ...newCodes[category],
    {
      code: code,
      // Normalize modifier to lowercase for consistency with enum values
      modifier: modifier ? modifier.toLowerCase() : null,
      source: 'manual'
    }
  ];

  return {
    success: true,
    codes: newCodes,
    message: 'Code ajouté avec succès'
  };
}

/**
 * Remove a code from the codes structure
 * Shows warning if removing an auto-detected code
 *
 * @param {Object} codes - Current codes structure
 * @param {string} category - Category key
 * @param {string} code - Code to remove
 * @param {boolean} confirmed - Whether user confirmed removal
 * @returns {Object} Object with {success: boolean, codes: Object, message: string, needsConfirmation: boolean}
 */
export function removeCode(codes, category, code, confirmed = false) {
  if (!codes || !category || !code) {
    return {
      success: false,
      codes: codes,
      message: 'Paramètres invalides',
      needsConfirmation: false
    };
  }

  const categoryArray = codes[category] || [];
  const codeObj = categoryArray.find(c => c.code === code);

  if (!codeObj) {
    return {
      success: false,
      codes: codes,
      message: 'Code non trouvé',
      needsConfirmation: false
    };
  }

  // If auto-detected and not confirmed, ask for confirmation
  if (codeObj.source === 'auto' && !confirmed) {
    return {
      success: false,
      codes: codes,
      message: 'Ce code a été détecté automatiquement dans le texte. Confirmer la suppression ?',
      needsConfirmation: true
    };
  }

  // Remove the code
  const newCodes = { ...codes };
  newCodes[category] = categoryArray.filter(c => c.code !== code);

  return {
    success: true,
    codes: newCodes,
    message: 'Code supprimé avec succès',
    needsConfirmation: false
  };
}

/**
 * Update a code's modifier
 *
 * @param {Object} codes - Current codes structure
 * @param {string} category - Category key
 * @param {string} code - Code to update
 * @param {string|null} modifier - New modifier value
 * @returns {Object} Updated codes structure
 */
export function updateCodeModifier(codes, category, code, modifier) {
  if (!codes || !category || !code) {
    return codes;
  }

  const newCodes = { ...codes };
  const categoryArray = newCodes[category] || [];

  newCodes[category] = categoryArray.map(c => {
    if (c.code === code) {
      return {
        ...c,
        // Normalize modifier to lowercase for consistency with enum values
        modifier: modifier ? modifier.toLowerCase() : null
      };
    }
    return c;
  });

  return newCodes;
}

/**
 * Update a code's value (for numeric codes)
 *
 * @param {Object} codes - Current codes structure
 * @param {string} category - Category key
 * @param {string} code - Code to update
 * @param {number|null} value - New value
 * @returns {Object} Updated codes structure
 */
export function updateCodeValue(codes, category, code, value) {
  if (!codes || !category || !code) {
    return codes;
  }

  const newCodes = { ...codes };
  const categoryArray = newCodes[category] || [];

  newCodes[category] = categoryArray.map(c => {
    if (c.code === code) {
      return {
        ...c,
        value: value
      };
    }
    return c;
  });

  return newCodes;
}

/**
 * Get count of codes in a category
 *
 * @param {Object} codes - Codes structure
 * @param {string} category - Category key
 * @returns {number} Count of codes
 */
export function getCategoryCount(codes, category) {
  if (!codes || !codes[category]) return 0;
  return codes[category].length;
}

/**
 * Get total count of all codes
 *
 * @param {Object} codes - Codes structure
 * @returns {number} Total count
 */
export function getTotalCodeCount(codes) {
  if (!codes || typeof codes !== 'object') return 0;

  let total = 0;
  Object.keys(codes).forEach(category => {
    if (Array.isArray(codes[category])) {
      total += codes[category].length;
    }
  });

  return total;
}

/**
 * Check if codes structure is empty
 *
 * @param {Object} codes - Codes structure
 * @returns {boolean} True if empty
 */
export function isCodesEmpty(codes) {
  return getTotalCodeCount(codes) === 0;
}

export default {
  extractEchoCodes,
  mergeCodesWithExisting,
  addCodeManually,
  removeCode,
  updateCodeModifier,
  updateCodeValue,
  getCategoryCount,
  getTotalCodeCount,
  isCodesEmpty
};
